import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, PhoneCall } from 'lucide-react';
import { Button } from '../ui/button';
import { CONTACT } from '../../data/siteData';

const ParticleNetwork = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf, particles = [];
    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const count = Math.min(70, Math.floor(canvas.width / 22));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.45,
        vy: (Math.random() - 0.5) * 0.45,
        r: Math.random() * 1.6 + 0.8,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(77,163,255,0.55)';
        ctx.fill();
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < 130) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(77,163,255,${0.18 * (1 - d / 130)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.3]);

  return (
    <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center bg-[#0A192F] overflow-hidden pt-24 pb-20">
      <ParticleNetwork />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,102,204,0.22),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(77,163,255,0.10),transparent_50%)]" />

      <motion.div style={{ y, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#4DA3FF]/30 bg-[#4DA3FF]/10 text-[#4DA3FF] text-xs font-semibold uppercase tracking-[0.15em] mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4DA3FF] animate-pulse" />
            Pan-India Operations · 15+ Cities
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.08] tracking-tight font-heading">
            India&apos;s Workforce &amp; Operations Partner{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4DA3FF] to-[#7CC0FF]">for Scale</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
            From workforce deployment and AI data collection to hiring, BPO and robotics operations,
            WorkLynx helps businesses execute efficiently across India.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" data-testid="hero-cta-request-call" className="bg-[#0066CC] hover:bg-[#0052A3] text-white text-base px-8 h-13 h-[52px] group">
              <Link to="/#contact">
                Request a Call <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" data-testid="hero-cta-talk-expert"
              className="border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white text-base px-8 h-[52px] backdrop-blur-sm">
              <a href={CONTACT.calendly} target="_blank" rel="noopener noreferrer">
                <PhoneCall className="mr-2 w-4 h-4" /> Talk to an Expert
              </a>
            </Button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-slate-400">
            {['100,000+ Hours Executed', '500+ Workforce Managed', 'Fast Workforce Deployment', 'Dedicated Workforce Management'].map((t) => (
              <div key={t} className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-[#4DA3FF]" /> {t}
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
